import { api } from './api';
import { CATALOG } from './capabilities.catalog';
import type { Module, Capability } from './capabilities.catalog';
import type { RBACContext, RBACModuleContext, RBACRowScope } from '../api/client';

/**
 * Advisory client-side mirror of the server's RBAC policy.
 *
 * The server stays the sole authority: the (role × module) policy and
 * the (role × device_class) ceiling are enforced on every protected
 * call. This module only mirrors the *resolved* answer the server already sent,
 * so the SPA can hide or redirect views a user can't reach rather than show dead
 * ends. It never grants access — a bypassed guard still meets a server 403/404.
 *
 * The single source is GET /api/rbac/context: one fetch carrying the
 * caller's whole picture — per-module capabilities, reachable view nodes, field
 * group levels, row scope, and the active company. The server computes it with
 * the same resolvers its enforcement gates use, so the mirror cannot drift.
 * Before that payload arrives can()/canView()/fieldLevel() fail OPEN on absent
 * data (the bounded boot window, ADR-0010) — there is no static /api/me fallback
 * map, which would have been per-company-blind and could over-report (RBAC-008).
 *
 * The capability set is the composed (role × module) ∩ (role × device_class)
 * set for the session's bound device class, so a phone session already reflects
 * the phone ceiling. It is auth-bound — fixed for the session — not
 * viewport-bound: resizing the window changes layout, never capabilities (a
 * device-class switch forces re-auth, ).
 *
 * The context is per-COMPANY: switching company re-fetches it and broadcasts
 * RBAC_CONTEXT_EVENT so nav and screens re-render under the new role without a
 * page reload (RBAC-006.7).
 */

// The Fexo module catalog and capability vocabulary are generated from the
// server engine enum (RBAC-002) into capabilities.catalog.ts and re-exported
// here, so this advisory mirror can never drift from rbac.Module /
// rbac.Capability: eight active modules plus the four Phase-2 reserved ones,
// and the six capabilities. The context returns a row for every module, so the
// union lists them all even though only a subset is routable today.
// Existing callers keep importing { Module, Capability } from './capabilities'.
export type { Module, Capability };
export type { RBACContext, RBACModuleContext, RBACRowScope };

// FieldLevel is the three-rung field-group ladder, mirroring rbac.FieldAccess.
export type FieldLevel = 'none' | 'read' | 'write';

// Which module a client route belongs to. Only these routes are
// capability-gated; everything else — home, the Me hub, account settings, the
// public auth pages — is reachable by any signed-in user and is intentionally
// absent. Matching is prefix-aware so sub-pages (e.g. /work-orders/:id, added in
// a later milestone) inherit their parent's gate.
// The two admin routes gate on their own module, so an ops_manager — who holds
// admin_users but not admin_roles — sees Members and not Roles, rather than one
// coarse "admin" gate letting them into a screen every write would reject. The
// admin modules are uncataloged, so canViewPath resolves them through the
// module-level `view` capability (its "uncataloged inherits" branch); that
// capability is already device-composed by the server, so a phone-bound session
// carries no admin grant and the entries disappear on their own (FE-001.1).
// The platform-plane console routes (RBAC-010) gate on the `platform` module,
// which is None for every seed role and granted only through a company's
// per-company snapshot — so these entries make the HQ-only lenses disappear for
// non-HQ admins with zero bespoke logic, exactly as the admin modules do (the
// uncataloged branch of canViewPath resolves them through module-level `view`,
// already device-composed by the server → desk-only falls out on its own). The
// paths are pre-registered here ahead of the lens pages (API-006/API-007), which
// confirm the exact console slugs. The company-plane Activity lens (API-007.A) is
// mapped to admin_users, NOT platform — any company admin reaches it, so it carries
// a company-admin gate rather than the HQ-only platform gate.
const MODULE_FOR_PATH: Record<string, Module> = {
  '/work-orders': 'work_orders',
  '/trips': 'trips',
  '/reports': 'reports',
  '/admin/roles': 'admin_roles',
  '/admin/users': 'admin_users',
  // Company-plane Activity lens (API-007.A): gated on admin_users, so any company
  // admin reaches it — deliberately NOT the platform module (that would restrict
  // it to the HQ persona like Status/Security). Desk-only falls out on its own via
  // the module-level view being device-composed by the server.
  '/admin/activity': 'admin_users',
  '/admin/subdivisions': 'admin_subdivisions',
  // The counterparty catalog (issue 544) is gated on the `parties` module: it
  // publishes a `list` view node (API-016), so canViewPath gates on
  // parties.list and the prefix-aware match covers any sub-path.
  '/admin/parties': 'parties',
  // The location manager (FE-027) is gated on the `locations` module: it
  // publishes a `list` view node (API-010), so canViewPath gates on
  // locations.list and the prefix-aware match covers any sub-path.
  '/admin/locations': 'locations',
  // The catalog admin (FE-030) is gated on the `catalogs` module at MODULE
  // level: the module publishes no `list` node (only `synonym_admin`, which
  // gates one tab inside the page), so canViewPath resolves through the
  // "uncataloged inherits" branch — module-level `view`, device-composed by
  // the server, so desk-only falls out on its own.
  '/admin/catalogs': 'catalogs',
  '/admin/companies': 'admin_companies',
  '/admin/security': 'platform',
  '/admin/hq-roster': 'platform',
  '/admin/config': 'platform',
  '/admin/errors': 'platform',
  '/admin/status': 'platform',
  // The fleet lens (FE-032) rides the platform module like the other console
  // lenses — v1 gates at module level; the platform.fleet catalog node exists
  // server-side but no route enforces node granularity yet (API-015 as-built).
  '/admin/fleet': 'platform',
};

// Broadcast whenever the context payload changes — loaded at boot, re-fetched
// after a company switch, or cleared on sign-out. Nav surfaces and any
// capability-gated screen listen for it and re-render. Mirrors the HEALTH_EVENT
// pattern in api/client.ts — the in-repo precedent for a reactive app signal.
export const RBAC_CONTEXT_EVENT = 'app:rbac-context';

let context: RBACContext | null = null;

// rbacContext exposes the raw payload for surfaces that need more than a
// yes/no — the active company, the membership list, a module's row scope.
export function rbacContext(): RBACContext | null {
  return context;
}

function publish(next: RBACContext | null): void {
  context = next;
  window.dispatchEvent(new CustomEvent<RBACContext | null>(RBAC_CONTEXT_EVENT, { detail: next }));
}

// setRbacContext installs a payload directly. The loader below is the normal
// path; this exists for tests and for a caller that already holds the payload.
export function setRbacContext(next: RBACContext | null): void {
  publish(next);
}

// clearRbacContext drops the mirror — on sign-out, so a subsequent session never
// renders against the previous user's grants.
export function clearRbacContext(): void {
  publish(null);
}

// loadRbacContext fetches the caller's context and broadcasts it. Best-effort:
// a failure leaves the previous payload in place rather than blanking the UI,
// since the server enforces regardless and a blank mirror would strand the user
// on a nav-less screen. Returns the payload, or null when the fetch failed.
export async function loadRbacContext(): Promise<RBACContext | null> {
  try {
    const next = await api<RBACContext>('/api/rbac/context');
    publish(next);
    return next;
  } catch {
    return null;
  }
}

function moduleRow(module: Module): RBACModuleContext | null {
  return context?.modules?.[module] ?? null;
}

/**
 * can reports whether the signed-in user holds `cap` on `module`.
 *
 * The context payload is the only source. Fail-open only before it loads: hiding
 * a feature the user may well have would flash an empty shell, and the server
 * enforces regardless. Once the payload is present the mirror is authoritative
 * for UX, and a missing or empty module entry means "hide". There is no static
 * fallback map — one that ignored per-company role docs could over-report, which
 * ADR-0010 forbids (RBAC-008).
 */
export function can(module: Module, cap: Capability): boolean {
  const row = moduleRow(module);
  if (row) return row.capabilities.includes(cap);
  if (context) return false; // context loaded but module absent — deny
  return true; // no payload yet (boot window) — don't hide; server enforces
}

// canViewModule is the gate for *reaching* a module surface (nav + route
// guard): view is the floor of every non-empty cell, so it answers "should this
// module be visible at all".
export function canViewModule(module: Module): boolean {
  return can(module, 'view');
}

// splitNode splits a catalog node path into its module and the remainder, or
// null when the path is malformed or names a module outside the vocabulary —
// mirroring the server's parseViewNode, so a node the server would reject never
// resolves open here either.
function splitNode(node: string): { module: Module; rest: string } | null {
  const dot = node.indexOf('.');
  if (dot <= 0 || dot >= node.length - 1) return null;
  const module = node.slice(0, dot) as Module;
  if (!CATALOG.some((m) => m.module === module)) return null;
  return { module, rest: node.slice(dot + 1) };
}

/**
 * canView answers whether the caller may reach a catalog view node —
 * `canView('work_orders.approval_queue')` (RBAC-006.6).
 *
 * The server already resolved each node against the view gate (the role's hides
 * and the node's cross-module `requires` composed with the device ceiling), so
 * the payload lists exactly the reachable ones. A node absent from that list —
 * hidden, ungated, or a typo — is denied, matching the server's fail-closed
 * view gate. Before any payload arrives this fails OPEN like can(), so boot
 * never flashes an empty shell.
 */
export function canView(node: string): boolean {
  if (!context) return true; // no mirror data — don't hide; server enforces
  const parsed = splitNode(node);
  if (!parsed) return false; // malformed or unknown module → deny
  return moduleRow(parsed.module)?.views.includes(parsed.rest) ?? false;
}

/**
 * fieldLevel resolves a catalog field group's level —
 * `fieldLevel('work_orders.fields.financial')` → 'none' | 'read' | 'write'
 * (RBAC-006.6). Forms render a 'read' group's inputs disabled and omit a 'none'
 * group entirely.
 *
 * Fails closed to 'none' for an unknown group, matching the server's resolver.
 * Before any payload arrives it answers 'write' — the fail-open posture can()
 * uses, so a form is never silently un-editable during boot; the server rejects
 * a forbidden write with 422 FIELD_FORBIDDEN regardless.
 */
export function fieldLevel(node: string): FieldLevel {
  if (!context) return 'write'; // no mirror data — don't disable; server enforces
  const parsed = splitNode(node);
  if (!parsed) return 'none';
  const group = parsed.rest.startsWith('fields.') ? parsed.rest.slice('fields.'.length) : null;
  if (!group) return 'none';
  return moduleRow(parsed.module)?.fields?.[group] ?? 'none';
}

// ── The field-group member table, loaded lazily ──────────────────────────────
//
// Field-group NAMES ride the main mirror (CATALOG), because nav gating and the
// role editor's shape need them at boot. The MEMBER lists do not: they are only
// consulted when a form asks about a specific property, and they are the one
// part of the catalog that grows without bound — one entry per annotated
// response leaf, across every module. They cost 1,045 gzipped bytes of the main
// bundle's 29,000-byte budget when this split was made, so they live in their own
// generated module and are imported dynamically.
//
// The import is fired at module evaluation, NOT on first use, so the chunk
// downloads in parallel with boot rather than at the moment a form first needs
// it. That matters for the fail-open window below: by the time a user has
// navigated to a form and the RBAC context has been fetched over the network,
// this has long since resolved.
let fieldMembers: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> | null = null;

const fieldMembersReady: Promise<void> = import('./capabilities.fields')
  .then((m) => {
    fieldMembers = m.FIELD_MEMBERS;
  })
  .catch(() => {
    // A failed chunk fetch leaves the table null, which fieldGroupOf treats as
    // "not governed" and fieldLevelOf therefore as editable. That is the same
    // fail-open posture the whole mirror takes on absent data (ADR-0010): the
    // server rejects a forbidden write with 422 FIELD_FORBIDDEN regardless, and
    // a form frozen read-only by a network hiccup would be worse than one that
    // lets the user try.
  });

/**
 * loadFieldMembers resolves once the field-group member table is available.
 *
 * Callers that MUST have it — the role editor, which renders each group's member
 * list as a hint, and tests asserting fieldGroupOf — await this. Ordinary form
 * code does not need to: it reads through fieldLevelOf, which fails open exactly
 * as it already does before the RBAC context arrives.
 */
export function loadFieldMembers(): Promise<void> {
  return fieldMembersReady;
}

// fieldGroupOf is the catalog join: which field group governs `field` on
// `module`, mirroring the server's Catalog.FieldGroupOf. Null when the property
// is not governed (an uncataloged module, or a field outside every group) — and
// also, briefly, before the lazily-loaded member table arrives, which the
// mirror's fail-open rule already covers.
export function fieldGroupOf(module: Module, field: string): string | null {
  if (!CATALOG.some((m) => m.module === module)) return null;
  const groups = fieldMembers?.[module];
  if (!groups) return null;
  for (const name in groups) {
    if (groups[name].includes(field)) return name;
  }
  return null;
}

// fieldLevelOf answers the question a form actually asks: what may this caller
// do with THIS property? Joins the generated catalog (which group owns the
// field) with the context payload (that group's level). An ungoverned field on
// a cataloged module cannot exist — the build-time parity gate rejects it — so
// a null group means the module simply isn't cataloged yet, and the field is
// left editable rather than mysteriously disabled.
export function fieldLevelOf(module: Module, field: string): FieldLevel {
  const group = fieldGroupOf(module, field);
  if (!group) return 'write';
  return fieldLevel(`${module}.fields.${group}`);
}

// moduleForPath returns the module a route is gated by, or null when the route
// is open to any signed-in user. Prefix-aware so a module's sub-pages share its
// gate.
export function moduleForPath(path: string): Module | null {
  for (const prefix in MODULE_FOR_PATH) {
    if (path === prefix || path.startsWith(prefix + '/')) return MODULE_FOR_PATH[prefix];
  }
  return null;
}

// A route that lands on a view node OTHER than the module's list — the review
// queue (FE-031) is gated on `trips.review_queue` (approve + the device
// ceiling), the GPS wall (re-homed to /trips/live by FE-029) on
// `trips.command_center`, both exact paths consulted before the `list`
// convention. The trip detail (`/trips/:id`, FE-029) is the one PATTERN: any
// single further segment under /trips that is not an exact entry lands on
// `trips.detail`. A path absent from both keeps the convention.
const LEAF_FOR_PATH: Record<string, string> = {
  '/trips/review': 'review_queue',
  '/trips/live': 'command_center',
};
const TRIP_DETAIL_PATH = /^\/trips\/[^/]+$/;

export function leafForPath(path: string): string {
  const exact = LEAF_FOR_PATH[path];
  if (exact) return exact;
  return TRIP_DETAIL_PATH.test(path) ? 'detail' : 'list';
}

// viewNodeForPath returns the catalog view node a gated route lands on, or null
// when the module has no such node in the catalog. The convention is the
// module's list view — a nav entry points at a module's index screen — so a
// module gains node-level nav gating simply by being annotated, with no mapping
// table to keep in sync; LEAF_FOR_PATH names the exceptions.
function viewNodeForPath(module: Module, leaf = 'list'): string | null {
  const mc = CATALOG.find((m) => m.module === module);
  if (!mc?.views.some((v) => v.name === leaf)) return null;
  return `${module}.${leaf}`;
}

// canViewPath answers whether the signed-in user may reach `path`.
//
// Resolution follows the engine's §4.2 rule — most-specific wins, an unset node
// inherits: an open route (absent from the gate map) is always allowed; a gated
// route whose module publishes a `list` view node is gated on THAT node, so a
// role that holds the module but has the screen hidden is correctly dropped from
// the nav; and a module with no catalog node yet inherits the module-level
// `view` capability. Uncataloged must mean "inherit", never "deny" — gating on a
// node the catalog doesn't have yet would empty the nav for every module that
// has not been annotated.
export function canViewPath(path: string): boolean {
  const module = moduleForPath(path);
  if (module === null) return true;
  // Node-level gating needs the context payload: it is the only source that
  // says which nodes are reachable. Without it (the boot window, before the
  // payload loads) there is nothing more specific than the module answer —
  // consulting canView here would hit its fail-open branch and bypass the
  // module check entirely.
  const node = context ? viewNodeForPath(module, leafForPath(path)) : null;
  return node === null ? canViewModule(module) : canView(node);
}
